import React from "react"
import { Box, Button, CircularProgress, Container, Tab, Tabs, Theme, makeStyles } from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import type { TFunction } from "i18next"
import { ArrowUpRight } from "react-feather"
import { useTranslation } from "react-i18next"
import { useAppContext } from "../app-context/use-app-context"
import ApproveHours from "../hour-reports/components/ApproveHours/ApproveHours"
import ReportedHours from "../hour-reports/components/ReportedHours/ReportedHours"
import HourReports from "../hour-reports/hour-reports"
import { useRouter } from "../hooks"
import { useUrlWithContext } from "../hooks/use-url-with-context"
import { paths } from "../paths"
import type { AppMainContextType } from "../types/app-context"
import { useHourReportingWorkflowSettings } from "../hour-reports/hooks/use-hour-reporting-workflow-settings"
import type { HourReportingWorkflowSettingsProps } from "../hour-reports/hooks/use-hour-reporting-workflow-settings"
import { useAuthUserMembership } from "../memberships/hooks/use-auth-user-membership"

const tabs: readonly HourReportTab[] = [
  {
    kind: "content",
    value: "report-hours",
    Component: HourReports,
    isAvailable: ({ contextType, canReportOwnHoursAsMember }) => contextType === "user" || canReportOwnHoursAsMember,
  },
  {
    kind: "content",
    value: "reported-hours",
    Component: ReportedHours,
    isAvailable: () => true,
  },
  {
    kind: "content",
    value: "approve-hours",
    Component: ApproveHours,
    isAvailable: ({ contextType, workflowSettings }) =>
      contextType === "org" && Boolean(workflowSettings?.supportsApproval),
  },
  {
    kind: "link",
    value: "hour-reporting-settings",
    isAvailable: ({ contextType, canUpdateSettings }) => contextType === "org" && canUpdateSettings,
  },
]

const getHourReportTabLabel = (value: HourReportTabValue, t: TFunction) => {
  switch (value) {
    case "report-hours":
      return t("tabs.reportHours", { defaultValue: "Report hours" })
    case "reported-hours":
      return t("tabs.reportedHours", { defaultValue: "Reported hours" })
    case "approve-hours":
      return t("tabs.approveHours", { defaultValue: "Approve hours" })
    case "hour-reporting-settings":
      return t("tabs.settings", { defaultValue: "Hour reporting settings" })
  }
}

const HourReportRoute = () => {
  const { t } = useTranslation("hour-report")
  const classes = useStyles()
  const router = useRouter()
  const { appContext } = useAppContext()
  const { canReportOwnHoursAsMember } = useAuthUserMembership()
  const { createPathWithContext } = useUrlWithContext()
  const orgId = appContext.mainContext?.type === "org" ? appContext.mainContext.id : ""
  const workflowSettingsQuery = useHourReportingWorkflowSettings(orgId)
  const query = router.query as { tab?: string }
  const requestedTab = typeof query.tab === "string" ? query.tab : undefined
  const availableTabs = getAvailableHourReportTabs(
    appContext.mainContext?.type,
    workflowSettingsQuery.settings,
    workflowSettingsQuery.canUpdateSettings,
    Boolean(canReportOwnHoursAsMember)
  )
  const activeTab = resolveActiveHourReportTab(requestedTab, availableTabs)
  const ActiveTabComponent = activeTab.Component
  const hourReportingSettingsPath = createPathWithContext(paths.orgHourReportSettings())

  React.useEffect(() => {
    if (workflowSettingsQuery.isLoading || !requestedTab || requestedTab === activeTab.value) return

    router.replace({ search: setHourReportTabSearch(router.location.search, activeTab.value) })
  }, [activeTab.value, requestedTab, router, workflowSettingsQuery.isLoading])

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: HourReportTabValue) => {
    const selectedTab = availableTabs.find((tab) => tab.value === newValue)
    if (!selectedTab) return

    if (selectedTab.kind === "link") {
      router.push(hourReportingSettingsPath)
      return
    }

    router.push({ search: setHourReportTabSearch(router.location.search, selectedTab.value) })
  }

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Tabs
        variant="scrollable"
        value={activeTab.value}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        {availableTabs.map((tab) => {
          const label = getHourReportTabLabel(tab.value, t)

          return (
            <Tab
              key={tab.value}
              value={tab.value}
              className={tab.kind === "link" ? `${classes.tab} ${classes.linkTab}` : classes.tab}
              disableRipple
              label={tab.kind === "link" ? <LinkTabLabel label={label} /> : label}
            />
          )
        })}
      </Tabs>
      {orgId && workflowSettingsQuery.isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={24} />
        </Box>
      ) : orgId && (workflowSettingsQuery.isError || !workflowSettingsQuery.settings) ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={workflowSettingsQuery.refetch}>
              Retry
            </Button>
          }
        >
          Error loading hour report settings.
        </Alert>
      ) : (
        <ActiveTabComponent workflowSettings={workflowSettingsQuery.settings} />
      )}
    </Container>
  )
}

export const getAvailableHourReportTabs = (
  contextType: AppMainContextType["type"] | undefined,
  workflowSettings?: HourReportingWorkflowSettingsProps["workflowSettings"],
  canUpdateSettings = false,
  canReportOwnHoursAsMember = false
) => {
  const effectiveContext = contextType ?? "user"
  return tabs.filter((tab) =>
    tab.isAvailable({ contextType: effectiveContext, workflowSettings, canUpdateSettings, canReportOwnHoursAsMember })
  )
}

export const resolveActiveHourReportTab = (
  requestedTab: string | undefined,
  availableTabs: readonly HourReportTab[]
): ContentTab => {
  const requestedContentTab = availableTabs.find(
    (tab): tab is ContentTab => tab.kind === "content" && tab.value === requestedTab
  )

  if (requestedContentTab) return requestedContentTab

  const defaultTab = availableTabs.find((tab): tab is ContentTab => tab.kind === "content")

  if (!defaultTab) throw new Error("No hour report content tabs are available")
  return defaultTab
}

export const setHourReportTabSearch = (search: string, tab: ContentTabValue) => {
  const searchParams = new URLSearchParams(search)
  searchParams.set("tab", tab)
  return searchParams.toString()
}

const LinkTabLabel = ({ label }: { label: string }) => {
  const classes = useStyles()

  return (
    <span className={classes.linkTabLabel}>
      {label}
      <ArrowUpRight size={14} />
    </span>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(4),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  tab: {
    minWidth: 120,
  },
  linkTab: {
    color: theme.palette.primary.main,
  },
  linkTabLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: "inherit",
  },
}))

type ContextType = AppMainContextType["type"]
type ContentTabValue = "report-hours" | "reported-hours" | "approve-hours"
type LinkTabValue = "hour-reporting-settings"
type HourReportTabValue = ContentTabValue | LinkTabValue

type TabBase = {
  isAvailable: (options: TabAvailabilityOptions) => boolean
}

type TabAvailabilityOptions = {
  contextType: ContextType
  workflowSettings?: HourReportingWorkflowSettingsProps["workflowSettings"]
  canUpdateSettings: boolean
  canReportOwnHoursAsMember: boolean
}

type ContentTab = TabBase & {
  kind: "content"
  value: ContentTabValue
  Component: React.ComponentType<HourReportTabProps>
}

type LinkTab = TabBase & {
  kind: "link"
  value: LinkTabValue
}

type HourReportTab = ContentTab | LinkTab

export type HourReportTabProps = HourReportingWorkflowSettingsProps

export default HourReportRoute
